import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Auth/firebase";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../context/AuthContext"; // Import useAuth

const AddProject = () => {
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(new Date());
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [error, setError] = useState("");
  const [formFilled, setFormFilled] = useState(false); // State to track form completion
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth(); // Get the current user

  useEffect(() => {
    const fetchProject = async () => {
      if (id) {
        const docRef = doc(db, "projects", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const project = docSnap.data();
          setProjectName(project.name);
          setDescription(project.description);
          setDueDate(new Date(project.dueDate));
          setSelectedCategory(project.category);
        }
      }
    };
    fetchProject();
  }, [id]);

  const fetchCategories = async () => {
    const categoryCollection = collection(db, "categories");
    const categorySnapshot = await getDocs(categoryCollection);
    const categoriesList = categorySnapshot.docs.map((doc) => doc.data().name);
    setCategories(categoriesList);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Check if all fields are filled
    if (projectName && description && dueDate && selectedCategory) {
      setFormFilled(true);
    } else {
      setFormFilled(false);
    }
  }, [projectName, description, dueDate, selectedCategory]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formFilled) {
      setError("Please fill in all fields.");
      return;
    }
    if (dueDate < new Date()) {
      setError("Due date cannot be set in the past.");
      return;
    }
    const project = {
      name: projectName,
      description,
      dueDate,
      category: selectedCategory,
      assignees: [currentUser.uid], // Add the current user to assignees
      createdAt: new Date(),
      creator: {
        uid: currentUser.uid,
        email: currentUser.email,
      },
    };
    if (id) {
      const docRef = doc(db, "projects", id);
      await updateDoc(docRef, project);
    } else {
      await addDoc(collection(db, "projects"), project);
    }
    navigate("/projects");
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded-lg w-full max-w-lg space-y-6"
      >
        <h2 className="text-xl font-semibold text-center">
          {id ? "Edit Project" : "Add Project"}
        </h2>
        {error && <p className="text-red-500">{error}</p>}
        <div>
          <label className="block mb-2 text-sm font-medium">Project Name</label>
          <input
            type="text"
            placeholder="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Description</label>
          <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Due Date</label>
          <DatePicker
            selected={dueDate}
            onChange={(date) => setDueDate(date)}
            minDate={new Date()} // Set minDate to today
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          >
            <option value="">Select a Category</option>
            {categories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className={`w-full bg-blue-500 text-white py-2 rounded-lg ${
            formFilled ? "" : "cursor-not-allowed opacity-50"
          }`}
          disabled={!formFilled} // Disable button if form is not filled
        >
          {id ? "Update Project" : "Create Project"}
        </button>
      </form>
    </div>
  );
};

export default AddProject;
